import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Suspense, lazy, useState } from 'react';
import { z } from 'zod';
import { toast } from 'sonner';
import { MapPin, Pencil, Trash2 } from 'lucide-react';
import type { Land, LandCreateInput, LandUpdateInput } from 'shared/schemas';
import { Button } from '../../../components/ui/button.tsx';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog.tsx';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../../../components/ui/sheet.tsx';
import { GradientCard } from '../../../components/layout/GradientCard.tsx';
import { KpiCard } from '../../../features/dashboard/components/KpiCard.tsx';
import { KpiStrip } from '../../../features/dashboard/components/KpiStrip.tsx';
import { PageHeader } from '../../../components/layout/PageHeader.tsx';
import { LandDeleteDialog } from '../../../features/lands/components/LandDeleteDialog.tsx';
import { LandDetailCard } from '../../../features/lands/components/LandDetailCard.tsx';
import { LandForm } from '../../../features/lands/components/LandForm.tsx';
import { LandStatusBadge } from '../../../features/lands/components/LandStatusBadge.tsx';
import { useLand } from '../../../features/lands/hooks/useLand.ts';
import { useLands } from '../../../features/lands/hooks/useLands.ts';
import { useLandsStats } from '../../../features/lands/hooks/useLandsStats.ts';
import { useCreateLand } from '../../../features/lands/hooks/useCreateLand.ts';
import { useUpdateLand } from '../../../features/lands/hooks/useUpdateLand.ts';
import { STRINGS } from '../../../features/lands/lands.strings.ts';

const LandsOverviewMap = lazy(
  () => import('../../../features/map/components/LandsOverviewMap.tsx'),
);

const landsSearchSchema = z.object({
  view: z.string().optional(),
  edit: z.string().optional(),
  new: z.boolean().optional(),
});

export const Route = createFileRoute('/_authed/lands/')({
  validateSearch: (s) => landsSearchSchema.parse(s),
  component: LandsPage,
});

function LandHeroCard({
  land,
  onClick,
}: {
  land: Land;
  onClick: () => void;
}) {
  return (
    <GradientCard
      className="flex w-56 shrink-0 cursor-pointer flex-col gap-3 p-4"
      onClick={onClick}
      data-testid="land-card"
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onClick();
      }}
    >
      {/* Media placeholder */}
      <div className="flex h-28 w-full items-center justify-center rounded-md bg-muted">
        <MapPin className="size-10 text-muted-foreground/50" />
      </div>

      {/* Content */}
      <div className="flex flex-col gap-1">
        <p className="truncate text-sm font-semibold">{land.name}</p>
        {land.areaHa != null && (
          <p className="text-xs text-muted-foreground">{land.areaHa.toLocaleString()} ha</p>
        )}
      </div>

      {/* Status badge */}
      <LandStatusBadge status={land.status} />
    </GradientCard>
  );
}

function LandSheetDetail({
  landId,
  onEdit,
  onDelete,
}: {
  landId: string;
  onEdit: () => void;
  onDelete: (land: Land) => void;
}) {
  const { data, isLoading } = useLand(landId);
  const land = data?.data;

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!land) {
    return (
      <div className="p-6 text-sm text-muted-foreground">No se encontró el terreno.</div>
    );
  }

  return (
    <div className="overflow-y-auto p-6">
      <LandDetailCard
        land={land}
        onEdit={onEdit}
        onDelete={() => onDelete(land)}
      />
    </div>
  );
}

function LandsPage() {
  const navigate = useNavigate();
  const [landToDelete, setLandToDelete] = useState<Land | null>(null);

  const { view: selectedId, new: isNewOpen, edit: editId } = Route.useSearch();

  const { data, isLoading } = useLands({ page: 1 });
  const stats = useLandsStats();
  const lands = data?.data ?? [];

  const createLand = useCreateLand();
  const updateLand = useUpdateLand(editId ?? '');

  function openNew() {
    void navigate({ to: '/lands', search: { new: true }, replace: true });
  }

  function closeModals() {
    void navigate({ to: '/lands', search: {}, replace: true });
  }

  function selectLand(land: Land) {
    void navigate({ to: '/lands', search: { view: land.id }, replace: true });
  }

  function handleCreate(formData: LandCreateInput) {
    createLand.mutate(formData, {
      onSuccess: () => {
        toast.success(STRINGS.TOAST_CREATE_SUCCESS);
        closeModals();
      },
      onError: () => {
        toast.error(STRINGS.TOAST_ERROR_GENERIC);
      },
    });
  }

  function handleUpdate(formData: LandUpdateInput) {
    updateLand.mutate(formData, {
      onSuccess: () => {
        toast.success(STRINGS.TOAST_UPDATE_SUCCESS);
        closeModals();
      },
      onError: () => {
        toast.error(STRINGS.TOAST_ERROR_GENERIC);
      },
    });
  }

  const editLand = editId ? lands.find((l) => l.id === editId) : undefined;

  return (
    <div className="flex-1 overflow-auto px-3 sm:px-6 py-4 sm:py-6">
      <PageHeader
        title={STRINGS.LIST_TITLE}
        actions={
          <Button type="button" onClick={openNew}>
            {STRINGS.NEW_BUTTON}
          </Button>
        }
      />

      <KpiStrip>
        <KpiCard label="Total" value={String(stats.total)} />
        <KpiCard label={STRINGS.STATUS_ACTIVE} value={String(stats.activos)} />
        <KpiCard label={STRINGS.STATUS_RENTED} value={String(stats.alquilados)} />
        <KpiCard label={STRINGS.STATUS_FOR_SALE} value={String(stats.enVenta)} />
      </KpiStrip>

      <div className="mt-6 flex flex-col gap-6">
        {/* Map hero */}
        <div className="overflow-hidden rounded-lg border border-border shadow-sm" style={{ height: '480px' }}>
          <Suspense
            fallback={
              <div className="h-full w-full animate-pulse bg-muted" />
            }
          >
            {!isLoading && <LandsOverviewMap lands={lands} />}
          </Suspense>
        </div>

        {/* Horizontal scrolling cards */}
        {lands.length > 0 && (
          <div className="flex gap-4 overflow-x-auto pb-2">
            {lands.map((land) => (
              <LandHeroCard
                key={land.id}
                land={land}
                onClick={() => selectLand(land)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Land detail sheet */}
      <Sheet
        open={Boolean(selectedId)}
        onOpenChange={(open) => { if (!open) closeModals(); }}
      >
        <SheetContent side="right" className="w-full max-w-lg overflow-hidden p-0">
          {selectedId && (
            <>
              <SheetHeader className="border-b border-border p-6 pb-4">
                <div className="flex items-center justify-between gap-3">
                  <SheetTitle className="truncate">
                    {lands.find((l) => l.id === selectedId)?.name ?? STRINGS.DETAIL_TITLE}
                  </SheetTitle>
                  <div className="flex shrink-0 gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        void navigate({ to: '/lands', search: { edit: selectedId }, replace: true })
                      }
                    >
                      <Pencil className="size-3.5" />
                    </Button>
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      onClick={() => {
                        const land = lands.find((l) => l.id === selectedId);
                        if (land) setLandToDelete(land);
                      }}
                    >
                      <Trash2 className="size-3.5" />
                    </Button>
                  </div>
                </div>
              </SheetHeader>

              <LandSheetDetail
                landId={selectedId}
                onEdit={() =>
                  void navigate({ to: '/lands', search: { edit: selectedId }, replace: true })
                }
                onDelete={(land) => setLandToDelete(land)}
              />
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* New land dialog */}
      <Dialog
        open={isNewOpen === true}
        onOpenChange={(open) => { if (!open) closeModals(); }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{STRINGS.NEW_BUTTON}</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <LandForm
              mode="create"
              formId="land-create-form"
              onSubmit={handleCreate}
              isPending={createLand.isPending}
            />
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeModals}>
              {STRINGS.ACTION_CANCEL}
            </Button>
            <Button type="submit" form="land-create-form" disabled={createLand.isPending}>
              {STRINGS.ACTION_SAVE}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit land dialog */}
      <Dialog
        open={editLand !== null && editLand !== undefined}
        onOpenChange={(open) => { if (!open) closeModals(); }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{STRINGS.EDIT_TITLE}</DialogTitle>
          </DialogHeader>
          {editLand && (
            <>
              <DialogBody>
                <LandForm
                  mode="edit"
                  formId="land-edit-form"
                  initialValues={editLand}
                  onSubmit={handleUpdate}
                  isPending={updateLand.isPending}
                />
              </DialogBody>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={closeModals}>
                  {STRINGS.ACTION_CANCEL}
                </Button>
                <Button type="submit" form="land-edit-form" disabled={updateLand.isPending}>
                  {STRINGS.ACTION_SAVE}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete dialog */}
      <LandDeleteDialog
        land={landToDelete}
        open={landToDelete !== null}
        onClose={() => {
          setLandToDelete(null);
          closeModals();
        }}
      />
    </div>
  );
}
